import { useEffect, useRef, useState } from "react";
import { SPOTIFY_SHOWS, spotifyUrlFor } from "./spotifyShows";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SAMPLE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/book/download/audio/voice_test_fable.mp3`;

/**
 * Hero-area Spotify CTA: a single conversion pill placed directly under the
 * book title. It auto-counts how many language editions are live on Spotify
 * and grows the badge ("Now in 1 language" → "Now in 5 languages") as more
 * shows are added to `spotifyShows.js`.
 *
 * Includes a 20-sec narrator-sample play button so first-time visitors can
 * taste the voice without leaving the page.
 */
export default function HeroSpotifyCTA() {
  const [textCount, setTextCount] = useState(0);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/book/translations`)
      .then((r) => r.json())
      .then((d) => {
        // The /translations endpoint already includes English as one of
        // the entries, so we count text-available languages directly —
        // no +1 (which previously caused a "Read in 13 languages" off-by-one bug).
        const live = (d.translations || []).filter((t) => t.chapter_count > 0).length;
        setTextCount(live);
      })
      .catch(() => setTextCount(1));
  }, []);

  const englishUrl = spotifyUrlFor("en");
  const audioLangCount = Object.values(SPOTIFY_SHOWS).filter(Boolean).length;

  if (!englishUrl) return null;

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play();
    } else {
      el.pause();
    }
  };

  return (
    <div className="hero-spotify" data-testid="hero-spotify">
      <div className="hero-spotify-row">
        <button
          type="button"
          className={"hero-sample-btn" + (playing ? " hero-sample-btn-playing" : "")}
          onClick={togglePlay}
          data-testid="hero-sample-btn"
          aria-label={playing ? "Pause narrator sample" : "Play 20-second narrator sample"}
        >
          <span className="hero-sample-icon" aria-hidden="true">
            {playing ? (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <rect x="6" y="5" width="4" height="14" rx="1" />
                <rect x="14" y="5" width="4" height="14" rx="1" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </span>
          <span className="hero-sample-text">
            <span className="hero-sample-label">{playing ? "Playing…" : "Hear the voice"}</span>
            <span className="hero-sample-sub">20-sec sample</span>
          </span>
          {playing && (
            <span className="hero-sample-eq" aria-hidden="true">
              <span></span><span></span><span></span><span></span>
            </span>
          )}
        </button>

        <a
          href={englishUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="hero-spotify-pill"
          data-testid="hero-spotify-pill"
        >
          <span className="hero-spotify-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
              <path d="M12 0a12 12 0 100 24 12 12 0 000-24zm5.5 17.3a.75.75 0 01-1 .25c-2.8-1.7-6.4-2-10.5-1.1a.75.75 0 11-.4-1.45c4.5-1 8.4-.6 11.6 1.3a.75.75 0 01.3 1zm1.5-3.3a.94.94 0 01-1.3.3c-3.2-2-8.1-2.6-11.9-1.4a.94.94 0 11-.55-1.8c4.4-1.3 9.8-.6 13.5 1.7a.94.94 0 01.25 1.2zm.15-3.4c-3.85-2.3-10.2-2.5-13.9-1.4a1.13 1.13 0 11-.65-2.15c4.25-1.3 11.3-1 15.7 1.6a1.13 1.13 0 11-1.15 1.95z"/>
            </svg>
          </span>
          <span className="hero-spotify-text">
            <span className="hero-spotify-label">Listen on Spotify</span>
            <span className="hero-spotify-sub">
              {audioLangCount > 1
                ? `Now in ${audioLangCount} languages`
                : "More languages launching soon"}
            </span>
          </span>
        </a>
      </div>

      {textCount > 1 && (
        <span className="hero-spotify-tag" data-testid="hero-spotify-tag">
          Read in {textCount} languages
        </span>
      )}

      <audio
        ref={audioRef}
        src={SAMPLE_URL}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        data-testid="hero-sample-audio"
      />
    </div>
  );
}
